import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  arquivos as api,
  absoluto,
  type ArquivoNaLista,
  type CategoriaNoBanco,
} from '../api/arquivos'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Campo } from '../componentes/Campo'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { useSessao } from '../auth/useSessao'
import { mensagemDeErro } from './mensagemDeErro'
import { marcaDaRota } from './marcaDaRota'
import './usuarios.css'
import './entregas.css'
import './arquivos.css'

const VAZIO = { titulo: '', categoria: '', descricao: '' }

/**
 * O banco de arquivos do atendimento (demanda 048 · `P-35`).
 *
 * Existia na Zintech, sumiu na migração, e o guia DE/PARA de 22/07 registrou
 * como *"não nativo"* na plataforma nova. É o material que o **atendente
 * humano** procura no meio de uma conversa — catálogo, tabela de papéis, guia de
 * acabamento.
 *
 * 🔴 **O gesto que a tela existe para permitir é o link**, não o download. Quem
 * atende precisa de algo para **colar na conversa**: um endereço que valha fora
 * da sessão do portal, expire, e não abra o arquivo da outra marca.
 */
export function BancoDeArquivos() {
  const { t } = useTranslation()
  const { pode } = useSessao()

  const marca = marcaDaRota() ?? ''
  const podeGerenciar = pode('ArquivoGerenciar')

  const [lista, setLista] = useState<ArquivoNaLista[] | null>(null)
  const [categorias, setCategorias] = useState<CategoriaNoBanco[]>([])
  const [termo, setTermo] = useState('')
  const [categoria, setCategoria] = useState('')
  const [procurado, setProcurado] = useState(false)

  const [falhou, setFalhou] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  const [form, setForm] = useState(VAZIO)
  const entrada = useRef<HTMLInputElement>(null)

  const [link, setLink] = useState<{ id: number; url: string } | null>(null)

  const carregar = useCallback(
    async (busca?: { termo?: string; categoria?: string }) => {
      setFalhou(false)
      try {
        setLista(await api.listar(marca, busca))
        setProcurado(Boolean(busca?.termo || busca?.categoria))
      } catch {
        setFalhou(true)
      }
    },
    [marca],
  )

  useEffect(() => {
    void carregar()
    api.categorias(marca).then(setCategorias).catch(() => setCategorias([]))
  }, [carregar, marca])

  async function publicar(evento: FormEvent) {
    evento.preventDefault()

    const arquivo = entrada.current?.files?.[0]
    if (!arquivo) return

    setSalvando(true)
    setErro(null)

    try {
      await api.publicar(marca, { ...form, arquivo })
      setForm(VAZIO)
      if (entrada.current) entrada.current.value = ''
      await carregar()
      api.categorias(marca).then(setCategorias).catch(() => setCategorias([]))
    } catch (falha) {
      setErro(mensagemDeErro(falha, t))
    } finally {
      setSalvando(false)
    }
  }

  async function gerarLink(arquivo: ArquivoNaLista) {
    setErro(null)

    try {
      const { link: relativo } = await api.link(marca, arquivo.id)
      setLink({ id: arquivo.id, url: absoluto(relativo) })
    } catch (falha) {
      setErro(mensagemDeErro(falha, t))
    }
  }

  async function alternar(arquivo: ArquivoNaLista) {
    await api.salvar(marca, arquivo.id, {
      titulo: arquivo.titulo,
      descricao: arquivo.descricao,
      categoria: arquivo.categoria,
      ativo: !arquivo.ativo,
    })

    await carregar()
  }

  if (falhou)
    return <Estado tipo="erro" acao={<Botao onClick={() => carregar()}>{t('comum.tentarDeNovo')}</Botao>} />

  if (!lista) return <Estado tipo="carregando" />

  return (
    <section className="usuarios">
      <header className="usuarios__cabecalho">
        <div>
          <h1 className="usuarios__titulo">{t('arquivos.titulo')}</h1>
          <p className="usuarios__descricao">{t('arquivos.descricao')}</p>
        </div>
      </header>

      {erro && <Aviso tom="perigo">{erro}</Aviso>}

      <Cartao titulo={t('arquivos.procurar')}>
        <form
          className="entregas__busca"
          onSubmit={(e) => {
            e.preventDefault()
            void carregar({ termo: termo.trim(), categoria })
          }}
        >
          <Campo
            rotulo={t('arquivos.termo')}
            ajuda={t('arquivos.termoDica')}
            value={termo}
            onChange={(e) => setTermo(e.target.value)}
          />

          <label className="usuarios__filtro">
            <span>{t('arquivos.categoria')}</span>
            <select value={categoria} onChange={(e) => setCategoria(e.target.value)}>
              <option value="">{t('arquivos.todasAsCategorias')}</option>
              {categorias.map((c) => (
                <option key={c.categoria} value={c.categoria}>
                  {c.categoria} ({c.total})
                </option>
              ))}
            </select>
          </label>

          <div className="entregas__acoes">
            <Botao type="submit">{t('arquivos.procurar')}</Botao>

            {procurado && (
              <Botao
                variante="secundario"
                onClick={() => {
                  setTermo('')
                  setCategoria('')
                  void carregar()
                }}
              >
                {t('arquivos.limpar')}
              </Botao>
            )}
          </div>
        </form>
      </Cartao>

      {podeGerenciar && (
        <Cartao titulo={t('arquivos.novo')}>
          <form className="entregas__form" onSubmit={publicar}>
            <Campo
              rotulo={t('arquivos.tituloDoArquivo')}
              obrigatorio
              value={form.titulo}
              onChange={(e) => setForm({ ...form, titulo: e.target.value })}
            />

            <Campo
              rotulo={t('arquivos.categoria')}
              obrigatorio
              ajuda={t('arquivos.categoriaDica')}
              list="categorias-do-banco"
              value={form.categoria}
              onChange={(e) => setForm({ ...form, categoria: e.target.value })}
            />

            <datalist id="categorias-do-banco">
              {categorias.map((c) => (
                <option key={c.categoria} value={c.categoria} />
              ))}
            </datalist>

            <label className="campo">
              <span className="campo__rotulo">{t('arquivos.descricaoDoArquivo')}</span>
              <textarea
                className="campo__entrada"
                rows={2}
                value={form.descricao}
                onChange={(e) => setForm({ ...form, descricao: e.target.value })}
              />
              <span className="entregas__nota">{t('arquivos.descricaoDica')}</span>
            </label>

            <label className="campo">
              <span className="campo__rotulo">
                {t('arquivos.arquivo')}
                <span className="campo__obrigatorio" aria-hidden="true">
                  *
                </span>
              </span>
              <input ref={entrada} type="file" accept="image/*,application/pdf" required />
              <span className="entregas__nota">{t('arquivos.arquivoDica')}</span>
            </label>

            <Botao type="submit" carregando={salvando}>
              {t('arquivos.publicar')}
            </Botao>
          </form>
        </Cartao>
      )}

      <Cartao titulo={t('arquivos.disponiveis')}>
        {lista.length === 0 ? (
          procurado ? (
            <Estado
              tipo="vazio"
              titulo={t('arquivos.semResultado')}
              descricao={t('arquivos.semResultadoDescricao')}
            />
          ) : (
            <Estado tipo="vazio" titulo={t('arquivos.vazio')} descricao={t('arquivos.vazioDescricao')} />
          )
        ) : (
          <ul className="entregas__lista">
            {lista.map((a) => (
              <li key={a.id} className={`entregas__item ${a.ativo ? '' : 'arquivos__item--inativo'}`}>
                <div className="entregas__dados">
                  <div className="entregas__linha">
                    <strong>{a.titulo}</strong>
                    <span className="arquivos__categoria">{a.categoria}</span>
                    {!a.ativo && <span className="arquivos__inativo">{t('arquivos.inativo')}</span>}
                  </div>

                  {a.descricao && <p className="entregas__campo">{a.descricao}</p>}

                  <p className="entregas__autoria">
                    {a.nomeOriginal} · {Math.max(1, Math.round(a.bytes / 1024))} kB
                  </p>

                  <div className="entregas__acoes">
                    {/* 🔴 O link vem antes do download: é ele que o atendente
                        cola na conversa. Baixar para reenviar é o caminho que a
                        Zintech obrigava, e é o que esta tela existe para evitar. */}
                    <Botao onClick={() => gerarLink(a)}>{t('arquivos.gerarLink')}</Botao>

                    <a className="arquivos__baixar" href={api.conteudo(marca, a.id)}>
                      {t('arquivos.abrir')}
                    </a>

                    {podeGerenciar && (
                      <Botao variante="secundario" onClick={() => alternar(a)}>
                        {a.ativo ? t('arquivos.retirar') : t('arquivos.reativar')}
                      </Botao>
                    )}
                  </div>

                  {link?.id === a.id && (
                    <div className="arquivos__link">
                      <label className="campo">
                        <span className="campo__rotulo">{t('arquivos.linkPronto')}</span>
                        <input className="campo__entrada" readOnly value={link.url} />
                      </label>
                      <p className="entregas__nota">{t('arquivos.linkExpira')}</p>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Cartao>
    </section>
  )
}
